export const guidedYAxisTickNumber = 5;

const calculateNiceAxisStep = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const exponent = Math.floor(Math.log10(value));
  const magnitude = 10 ** exponent;
  const normalised = value / magnitude;

  if (normalised <= 1) return magnitude;
  if (normalised <= 2) return 2 * magnitude;
  if (normalised <= 2.5) return 2.5 * magnitude;
  if (normalised <= 5) return 5 * magnitude;
  return 10 * magnitude;
};

const normaliseAxisNumber = (value: number): number => Number(value.toPrecision(12));

const calculateGuidedAxisStep = (value: number, forceInteger: boolean): number => {
  const step = calculateNiceAxisStep(value);
  if (!forceInteger) return step;
  if (step < 1) return 1;
  return Number.isInteger(step) ? step : Math.floor(step);
};

export const calculateGuidedYAxisMax = (value: number, forceInteger = false): number => {
  if (!Number.isFinite(value) || value <= 0) return 1;
  if (forceInteger && value < 10) {
    const step = value <= 3 ? 1 : 2;
    const roundedMax = Math.ceil(value / step) * step;
    return roundedMax > value ? roundedMax : roundedMax + step;
  }

  const step = calculateNiceAxisStep(value / (guidedYAxisTickNumber - 1));
  const roundedMax = Math.ceil(value / step) * step;
  const nextMax = roundedMax >= value ? roundedMax : roundedMax + step;
  const resolvedMax = forceInteger ? Math.ceil(nextMax) : nextMax;
  return normaliseAxisNumber(resolvedMax);
};

export const calculateGuidedYAxisTicks = (
  minValue: number,
  maxValue: number,
  forceInteger = false,
): number[] => {
  const min = Number.isFinite(minValue) ? minValue : 0;
  const max = Number.isFinite(maxValue) && maxValue > min ? maxValue : min + 1;
  const range = max - min;
  const step = calculateGuidedAxisStep(range / (guidedYAxisTickNumber - 1), forceInteger);
  const ticks = [normaliseAxisNumber(min)];
  let nextTick = min + step;
  let guard = 0;

  while (nextTick < max && guard < 100) {
    ticks.push(normaliseAxisNumber(nextTick));
    nextTick += step;
    guard += 1;
  }

  const normalisedMax = normaliseAxisNumber(max);
  if (ticks[ticks.length - 1] !== normalisedMax) {
    ticks.push(normalisedMax);
  }

  return ticks;
};
